import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  inject,
  OnInit,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmSeparatorDirective } from '@spartan-ng/helm/separator';
import { WizardDialogHeader } from '@shared/components/wizard-dialog-header/wizard-dialog-header';
import { WizardDialogFooter } from '@shared/components/wizard-dialog-footer/wizard-dialog-footer';
import { SuffixInput } from '@shared/components/suffix-input/suffix-input';
import { SelectField } from '@shared/components/select-field/select-field';
import { StepLead } from '@shared/components/step-lead/step-lead';
import {
  SummarySection,
  SummaryRow,
} from '@shared/components/summary-section/summary-section';
import { createWizardSteps } from '@shared/models/step-timeline.model';
import { AddressService } from '@shared/services/address.service';
import { Address, CreateAddressRequest } from '@shared/models/address.model';

@Component({
  selector: 'app-address-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ...BrnDialogImports,
    ...HlmDialogImports,
    HlmSeparatorDirective,
    WizardDialogHeader,
    WizardDialogFooter,
    SuffixInput,
    SelectField,
    StepLead,
    SummarySection,
    SummaryRow,
  ],
  templateUrl: './address-form.html',
  styleUrl: './address-form.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressForm implements OnInit {
  // Inputs
  isOpen = input.required<boolean>();
  organizationId = input.required<string>();
  editAddress = input<Address | null>(null); // For editing existing addresses

  // Outputs
  close = output<void>();
  addressSaved = output<Address>();

  // Services
  private readonly fb = inject(FormBuilder);
  private readonly addressService = inject(AddressService);

  // State
  form!: FormGroup;
  isSubmitting = signal(false);
  error = signal<string | null>(null);

  // Wizard state — Adressdaten → Zusammenfassung, matching the other creation dialogs
  private static readonly STEP_LABELS = ['Adressdaten', 'Zusammenfassung'];
  private static readonly STEP_LEADS = [
    'Adressdaten und optionale Gebäudeangaben.',
    'Prüfen Sie die Angaben vor dem Speichern.',
  ];

  currentStep = signal(1);
  wizardSteps = computed(() =>
    createWizardSteps(AddressForm.STEP_LABELS, this.currentStep()),
  );
  stepLead = computed(() => AddressForm.STEP_LEADS[this.currentStep() - 1]);

  /** Live form values, so the summary step re-renders as fields change. */
  private readonly formValue = signal<Record<string, unknown>>({});
  private labelOf = (
    list: { value: string; label: string }[],
    value: unknown,
  ) => list.find((o) => o.value === value)?.label ?? 'Nicht angegeben';

  summaryStreet = computed(() => {
    const v = this.formValue();
    return `${v['street'] || '—'} ${v['houseNumber'] || ''}`.trim();
  });
  summaryCity = computed(() => {
    const v = this.formValue();
    return `${v['postalCode'] || '—'} ${v['city'] || ''}`.trim();
  });
  summaryRoofType = computed(() =>
    this.labelOf(this.roofTypes, this.formValue()['roofType']),
  );
  summaryBuildingType = computed(() =>
    this.labelOf(this.buildingTypes, this.formValue()['buildingType']),
  );
  summarySolar = computed(() =>
    this.labelOf(this.solarOptions, this.formValue()['solarStatus']),
  );

  // Options
  roofTypes = [
    { value: 'flat', label: 'Flachdach' },
    { value: 'normal', label: 'Normales Dach' },
    { value: 'both', label: 'Gemischt' },
  ];

  buildingTypes = [
    { value: 'single_family', label: 'Einfamilienhaus' },
    { value: 'multi_family', label: 'Mehrfamilienhaus' },
  ];

  solarOptions = [
    { value: 'yes', label: 'Ja' },
    { value: 'no', label: 'Nein' },
  ];

  constructor() {
    // Use effect to reinitialize form when editAddress or isOpen changes
    effect(() => {
      const isOpen = this.isOpen();
      const address = this.editAddress();

      // Only reinitialize when dialog opens
      if (isOpen && this.form) {
        this.populateForm(address);
      }
    });
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      roofType: [null],
      street: ['', [Validators.required, Validators.minLength(2)]],
      houseNumber: ['', [Validators.required]],
      city: ['', [Validators.required, Validators.minLength(2)]],
      postalCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      buildingType: [null],
      solarStatus: [null],
    });

    // Mirror form values into a signal so the summary step stays live.
    this.formValue.set(this.form.value);
    this.form.valueChanges.subscribe((v) => this.formValue.set(v));

    // Populate with initial values if editing
    this.populateForm(this.editAddress());
  }

  nextStep(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.currentStep.set(2);
    this.error.set(null);
  }

  prevStep(): void {
    this.currentStep.set(1);
    this.error.set(null);
  }

  /** Jump back to an already-reached step (never skips ahead unvalidated). */
  jumpToStep(target: number): void {
    if (target > 0 && target <= this.currentStep()) {
      this.currentStep.set(target);
      this.error.set(null);
    }
  }

  private populateForm(address: Address | null): void {
    if (address) {
      this.form.patchValue({
        roofType: address.roofType || null,
        street: address.street || '',
        houseNumber: address.houseNumber || '',
        city: address.city || '',
        postalCode: address.postalCode || '',
        buildingType: address.isApartmentBuilding
          ? 'multi_family'
          : address.isApartmentBuilding === false
          ? 'single_family'
          : null,
        solarStatus:
          address.hasSolar === true
            ? 'yes'
            : address.hasSolar === false
            ? 'no'
            : null,
      });
    } else {
      this.form.reset({
        roofType: null,
        street: '',
        houseNumber: '',
        city: '',
        postalCode: '',
        buildingType: null,
        solarStatus: null,
      });
    }
    this.currentStep.set(1);
    this.error.set(null);
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    this.error.set(null);

    try {
      const formValue = this.form.value;

      const request: CreateAddressRequest = {
        street: formValue.street,
        houseNumber: formValue.houseNumber,
        city: formValue.city,
        postalCode: formValue.postalCode,
        country: 'DE', // Standardmäßig Deutschland
      };

      // Nur senden wenn Werte ausgewählt wurden
      if (formValue.roofType) {
        request.roofType = formValue.roofType;
      }

      if (formValue.buildingType) {
        request.isApartmentBuilding = formValue.buildingType === 'multi_family';
      }

      if (formValue.solarStatus) {
        request.hasSolar = formValue.solarStatus === 'yes';
      }

      let savedAddress: Address;

      if (this.editAddress()) {
        const editAddress = this.editAddress()!;
        const addressId = editAddress.addressId;
        if (!addressId) {
          throw new Error('Keine gültige Adress-ID zum Bearbeiten gefunden');
        }
        savedAddress = await this.addressService.updateAddress(
          this.organizationId(),
          addressId,
          request
        );
      } else {
        savedAddress = await this.addressService.createAddress(
          this.organizationId(),
          request
        );
      }

      this.addressSaved.emit(savedAddress);
      this.onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Fehler beim Speichern der Adresse';
      this.error.set(errorMessage);
      console.error('[AddressForm] Error saving address:', err);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  onClose(): void {
    this.form.reset();
    this.currentStep.set(1);
    this.error.set(null);
    this.close.emit();
  }

  getFieldError(fieldName: string): string | null {
    const field = this.form.get(fieldName);
    if (!field || !field.errors || !field.touched) return null;

    if (field.errors['required']) return 'Dieses Feld ist erforderlich';
    if (field.errors['minlength'])
      return `Mindestens ${field.errors['minlength'].requiredLength} Zeichen`;
    if (field.errors['pattern']) {
      if (fieldName === 'postalCode') {
        return 'Postleitzahl muss genau 5 Ziffern haben';
      }
      return 'Ungültiges Format';
    }

    return 'Ungültiger Wert';
  }

  // Handler für numerische Eingabe bei PLZ
  onPostalCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Nur Zahlen erlauben
    const numericValue = value.replace(/[^0-9]/g, '');

    // Maximal 5 Zeichen
    const limitedValue = numericValue.substring(0, 5);

    // Wert setzen wenn geändert
    if (input.value !== limitedValue) {
      input.value = limitedValue;
      this.form.get('postalCode')?.setValue(limitedValue);
    }
  }
}
